const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

export interface User {
  id: number;
  employeeName: string;
  designation?: string;
  department?: string;
  email: string;
  building?: string;
  floor?: string;
  extNo?: string;
  contactNo?: string;
  employeeId?: string;
  employeeCode?: string;
  location?: string;
  dateOfJoining?: string;
  dateOfBirth?: string;
  weddingAnniversary?: string;
  profilePicture?: string;
  roleId: number;
  roleName?: string;
  isActive: boolean;
  lastLogin?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface Role {
  id: number;
  name: string;
  description: string;
  permissions?: string[];
}

export interface CreateUserData {
  employeeName: string;
  email: string;
  designation?: string;
  department?: string;
  building?: string;
  floor?: string;
  extNo?: string;
  contactNo?: string;
  employeeCode?: string;
  location?: string;
  dateOfJoining?: string;
  dateOfBirth?: string;
  weddingAnniversary?: string;
  roleId?: number;
}

export interface UpdateUserData {
  employeeName?: string;
  email?: string;
  designation?: string;
  department?: string;
  building?: string;
  floor?: string;
  extNo?: string;
  contactNo?: string;
  employeeCode?: string;
  location?: string;
  dateOfJoining?: string;
  dateOfBirth?: string;
  weddingAnniversary?: string;
  roleId?: number;
}

class UsersService {
  private baseURL = `${API_BASE_URL}/users`;

  // Helper to get auth headers
  private getAuthHeaders() {
    const token = localStorage.getItem('jwtToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  // Get all users
  async getAllUsers(): Promise<User[]> {
    try {
      const response = await fetch(this.baseURL, {
        headers: this.getAuthHeaders()
      });
      const data = await response.json();
      return data.users || [];
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  // Get user by ID
  async getUserById(id: number): Promise<User | null> {
    try {
      const response = await fetch(`${this.baseURL}/${id}`, {
        headers: this.getAuthHeaders()
      });
      const data = await response.json();
      return data.user || null;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  }

  // Create new user
  async createUser(userData: CreateUserData): Promise<User> {
    try {
      const response = await fetch(this.baseURL, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(userData)
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create user');
      }
      return data.user;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  // Update user
  async updateUser(id: number, userData: UpdateUserData): Promise<void> {
    try {
      const response = await fetch(`${this.baseURL}/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(userData)
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  // Delete user
  async deleteUser(id: number): Promise<void> {
    try {
      const response = await fetch(`${this.baseURL}/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  // Toggle user status
  async toggleUserStatus(id: number): Promise<void> {
    try {
      const response = await fetch(`${this.baseURL}/${id}/toggle-status`, {
        method: 'PUT',
        headers: this.getAuthHeaders()
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to toggle user status');
      }
    } catch (error) {
      console.error('Error toggling user status:', error);
      throw error;
    }
  }

  // Get all roles
  async getAllRoles(): Promise<Role[]> {
    try {
      const response = await fetch(`${this.baseURL}/roles/all`, {
        headers: this.getAuthHeaders()
      });
      const data = await response.json();
      return data.roles || [];
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  }

  // Bulk upload users from CSV
  async bulkUploadUsers(users: any[]): Promise<any> {
    try {
      const response = await fetch(`${this.baseURL}/bulk-upload`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ users })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to bulk upload users');
      }
      return data;
    } catch (error) {
      console.error('Error bulk uploading users:', error);
      throw error;
    }
  }

}

export default new UsersService();
